import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../../shared/storage/firebase';
import type { School, SchoolContactWithId, Squad, User } from '../../../../types';
import type { PartnerLocation } from '../../../partners/types';
import SearchableSelect from '../shared/SearchableSelect';

type PacketDraft = {
  selectedTeacherId?: string;
  introNote?: string;
  schoolSpecificNotes?: string;
  otherNotes?: string;
  updatedAt?: number;
  updatedBy?: string;
};

type PacketValue = {
  draft?: PacketDraft;
  latestPdf?: { storagePath?: string; url?: string; generatedAt?: number; generatedBy?: string };
  sentAt?: number;
  sentBy?: string;
  sentTo?: { teacherId?: string; teacherName?: string; email?: string; slackEmail?: string } | null;
};

const SOP_TITLE = 'OG First Day Standard Operating Procedure';
const SOP_CHECKLIST_TITLE = 'Read through and complete this checklist for your first class';
const SOP_ITEMS: string[] = [
  'Read school specific instructions on web app or forwarded email before your class',
  'Double check location of the class (some schools have different campuses! Make sure you get to the right one)',
  'Plan ahead for potential traffic that may cause you to be late to a class, you should always plan to arrive at least 30min - 45min early for your first class',
  'Read “Details” page in your squad on web app',
  'Prepare your first lesson, the survey person is there to observe you, NOT assist you to teach',
  'Charge devices & pack HDMI & adaptor',
  'Bring ID to sign in at front desk',
  'Learn about arrival and dismissal procedure for the school (where to pick up kids and where to drop them off)',
  'Learn about bathroom rules',
  'Learn about your class location',
  'Ask for WIFI information',
  '(If this is a music technology class or requires student devices) Set up Chromebooks and log in with the squad code for each student',
  'Connect to WIFI',
  'Connect to projector through HDMI & adaptor OR airplay',
  'Click “start” to clock in: time tracking and location tracking to show that you are on-site and on-time',
  'Take attendance with Overture web app',
  'Take attendance with School specific app if applicable, only certain schools require this (Paper attendance sheet, Campbrain, Jumbula, School software, Homeroom etc.)',
  'Teach & have fun!',
];

function safeTrim(v: any): string {
  return String(v ?? '').trim();
}

function AutoGrowTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { style, onChange, value, ...rest } = props;
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    resize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [String(value ?? '')]);

  return (
    <textarea
      {...rest}
      ref={ref}
      value={value as any}
      onChange={(e) => {
        onChange?.(e);
        // Let the browser apply the new value before measuring.
        window.requestAnimationFrame(resize);
      }}
      style={{
        ...style,
        overflow: 'hidden',
        resize: 'none',
        minHeight: 34,
      }}
    />
  );
}

export default function FirstDayTeacherPacketLogistic(props: {
  squadId: string;
  partnerId: string;
  locationId: string;
  legacySchoolId?: string;
  packetValue: any;
  squad: Squad | null;
  location: PartnerLocation | null;
  legacySchool?: School | null;
  partnerContacts: SchoolContactWithId[];
  currentUserId: string;
}): React.ReactElement {
  const {
    squadId,
    partnerId,
    locationId,
    legacySchoolId,
    packetValue,
    squad,
    location,
    legacySchool,
    currentUserId,
  } = props;
  const site = location || legacySchool || null;

  const parsed: PacketValue = (packetValue && typeof packetValue === 'object') ? (packetValue as any) : {};
  const initialDraft: PacketDraft = (parsed.draft && typeof parsed.draft === 'object') ? (parsed.draft as any) : {};

  const [draft, setDraft] = useState<PacketDraft>(() => ({
    selectedTeacherId: safeTrim((initialDraft as any).selectedTeacherId),
    introNote: safeTrim((initialDraft as any).introNote),
    schoolSpecificNotes: safeTrim((initialDraft as any).schoolSpecificNotes),
    otherNotes: safeTrim((initialDraft as any).otherNotes),
  }));
  const [schoolDraft, setSchoolDraft] = useState<Record<string, any>>(() => ({
    wifiUsername: (site as any)?.wifiUsername || '',
    wifiPassword: (site as any)?.wifiPassword || '',
    arrivalProcedure: (site as any)?.arrivalProcedure || '',
    dismissalProcedure: (site as any)?.dismissalProcedure || '',
    lateArrivalProcedure: (site as any)?.lateArrivalProcedure || '',
    bathroomRules: (site as any)?.bathroomRules || '',
    hallwayRules: (site as any)?.hallwayRules || '',
    parkingInstructions: (site as any)?.parkingInstructions || '',
  }));
  const [squadDraft, setSquadDraft] = useState<Partial<Squad>>(() => ({
    classroomName: (squad as any)?.classroomName || '',
    hasProjectorOrTv: (squad as any)?.hasProjectorOrTv,
    projectorConnectionInstructions: (squad as any)?.projectorConnectionInstructions || '',
    additionalNotes: (squad as any)?.additionalNotes || '',
  }));
  const [busy, setBusy] = useState<'idle' | 'saving' | 'generating' | 'sending'>('idle');
  const [status, setStatus] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(() => safeTrim(parsed?.latestPdf?.url));
  const [previewStoragePath, setPreviewStoragePath] = useState<string>(() => safeTrim(parsed?.latestPdf?.storagePath));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const justSavedTimerRef = useRef<number | null>(null);
  const isBusy = busy !== 'idle';

  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const teachersSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'teacher')));
        const teachersData = teachersSnap.docs.map(d => ({ id: d.id, ...d.data() } as User));
        setTeachers(teachersData.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')));
      } catch (e: any) {
        console.error('Failed to load teachers:', e);
      } finally {
        setLoadingTeachers(false);
      }
    };
    void loadTeachers();
  }, []);

  const selectedTeacher = useMemo(() => {
    return teachers.find(t => t.id === draft.selectedTeacherId) || null;
  }, [teachers, draft.selectedTeacherId]);

  const teacherOptions = useMemo(() => {
    return teachers.map(t => ({
      id: t.id,
      label: `${t.displayName || 'Unnamed'} (${t.email || 'no email'})`,
    }));
  }, [teachers]);

  useEffect(() => {
    const next: PacketDraft = (parsed.draft && typeof parsed.draft === 'object') ? (parsed.draft as any) : {};
    setDraft((cur) => ({
      ...cur,
      selectedTeacherId: safeTrim((next as any).selectedTeacherId || cur.selectedTeacherId),
      introNote: (next as any).introNote ?? cur.introNote,
      schoolSpecificNotes: (next as any).schoolSpecificNotes ?? cur.schoolSpecificNotes,
      otherNotes: (next as any).otherNotes ?? cur.otherNotes,
    }));
    setPreviewUrl((u) => u || safeTrim(parsed?.latestPdf?.url));
    setPreviewStoragePath((p) => p || safeTrim(parsed?.latestPdf?.storagePath));
  }, [
    safeTrim((parsed as any)?.draft?.updatedAt),
    safeTrim((parsed as any)?.latestPdf?.generatedAt),
    safeTrim((parsed as any)?.sentAt),
  ]);

  // Keep canonical site fields in sync with live location updates.
  useEffect(() => {
    if (!site) return;
    if (isBusy) return;
    setSchoolDraft((cur) => {
      const next = {
        wifiUsername: (site as any)?.wifiUsername || '',
        wifiPassword: (site as any)?.wifiPassword || '',
        arrivalProcedure: (site as any)?.arrivalProcedure || '',
        dismissalProcedure: (site as any)?.dismissalProcedure || '',
        lateArrivalProcedure: (site as any)?.lateArrivalProcedure || '',
        bathroomRules: (site as any)?.bathroomRules || '',
        hallwayRules: (site as any)?.hallwayRules || '',
        parkingInstructions: (site as any)?.parkingInstructions || '',
      } as any;
      const curStr = JSON.stringify(cur || {});
      const nextStr = JSON.stringify(next || {});
      if (curStr !== '{}' && curStr !== nextStr) return cur;
      return next;
    });
  }, [(site as any)?.id]);

  // Keep squad-specific fields (classroom/projector) in sync with live squad updates.
  useEffect(() => {
    if (!squad) return;
    // Only sync down from Firestore when we're not actively editing.
    if (isBusy) return;
    setSquadDraft((cur) => {
      // Preserve in-progress edits for the current squad if the user has changed anything.
      // (We compute a simple "dirty" check here vs always clobbering the form.)
      const next = {
        classroomName: (squad as any)?.classroomName || '',
        hasProjectorOrTv: (squad as any)?.hasProjectorOrTv,
        projectorConnectionInstructions: (squad as any)?.projectorConnectionInstructions || '',
        additionalNotes: (squad as any)?.additionalNotes || '',
      } as any;
      const curStr = JSON.stringify(cur || {});
      const nextStr = JSON.stringify(next || {});
      if (curStr !== '{}' && curStr !== nextStr) return cur;
      return next;
    });
  }, [squad?.id]);

  const persistOpsNotes = async () => {
    if (!squadId) return;
    const now = Date.now();
    const payload: PacketDraft = {
      selectedTeacherId: safeTrim(draft.selectedTeacherId),
      introNote: safeTrim(draft.introNote),
      schoolSpecificNotes: safeTrim(draft.schoolSpecificNotes),
      otherNotes: safeTrim(draft.otherNotes),
      updatedAt: now,
      updatedBy: safeTrim(currentUserId),
    };
    await updateDoc(doc(db, 'squads', squadId), {
      firstDayTeacherPacket: {
        ...(parsed || {}),
        draft: payload,
      },
    } as any);
  };

  const persistLocationDetails = async () => {
    if (!locationId) return;
    await updateDoc(doc(db, 'locations', locationId), {
      wifiUsername: safeTrim((schoolDraft as any).wifiUsername),
      wifiPassword: String((schoolDraft as any).wifiPassword || ''), // passwords may contain spaces
      arrivalProcedure: String((schoolDraft as any).arrivalProcedure || ''),
      dismissalProcedure: String((schoolDraft as any).dismissalProcedure || ''),
      lateArrivalProcedure: String((schoolDraft as any).lateArrivalProcedure || ''),
      bathroomRules: String((schoolDraft as any).bathroomRules || ''),
      hallwayRules: String((schoolDraft as any).hallwayRules || ''),
      parkingInstructions: String((schoolDraft as any).parkingInstructions || ''),
    } as any);
  };

  const persistSquadDetails = async () => {
    if (!squadId) return;
    await updateDoc(doc(db, 'squads', squadId), {
      classroomName: String((squadDraft as any).classroomName || ''),
      hasProjectorOrTv: (squadDraft as any).hasProjectorOrTv ?? null,
      projectorConnectionInstructions: String((squadDraft as any).projectorConnectionInstructions || ''),
      additionalNotes: String((squadDraft as any).additionalNotes || ''),
      teacherDetailsUpdatedAt: Date.now(),
      teacherDetailsUpdatedBy: safeTrim(currentUserId) || null,
    } as any);
  };

  const markJustSaved = () => {
    setJustSaved(true);
    if (justSavedTimerRef.current) window.clearTimeout(justSavedTimerRef.current);
    justSavedTimerRef.current = window.setTimeout(() => setJustSaved(false), 900);
  };

  const saveAll = async () => {
    if (!squadId) return;
    setBusy('saving');
    setStatus(null);
    try {
      await persistLocationDetails();
      await persistSquadDetails();
      await persistOpsNotes();
      setLastSavedAt(Date.now());
      markJustSaved();
      setStatus('Saved.');
    } catch (e: any) {
      setStatus(String(e?.message || 'Failed to save.'));
    } finally {
      setBusy('idle');
    }
  };

  const generatePreview = async (opts?: { open?: boolean }) => {
    if (!squadId) return;
    setBusy('generating');
    setStatus(null);
    try {
      // Ensure SSOT fields + packet-only notes are saved before generating.
      await persistLocationDetails();
      await persistSquadDetails();
      await persistOpsNotes();
      setLastSavedAt(Date.now());
      const fn = httpsCallable(getFunctions(), 'generateFirstDayTeacherPacketPdf');
      const res: any = await fn({
        squadId,
        partnerId,
        locationId,
        ...(legacySchoolId ? { legacySchoolId } : {}),
        teacherId: safeTrim(draft.selectedTeacherId),
        // Only packet-only notes should travel as "overrides".
        overrides: {
          introNote: safeTrim(draft.introNote),
          schoolSpecificNotes: safeTrim(draft.schoolSpecificNotes),
          otherNotes: safeTrim(draft.otherNotes),
        },
      });
      const url = safeTrim(res?.data?.url);
      const storagePath = safeTrim(res?.data?.storagePath);
      if (url) setPreviewUrl(url);
      if (storagePath) setPreviewStoragePath(storagePath);
      // Persist latest PDF info onto the squad for audit + completeness checks.
      await updateDoc(doc(db, 'squads', squadId), {
        'firstDayTeacherPacket.latestPdf': {
          storagePath: storagePath || null,
          url: url || null,
          generatedAt: Date.now(),
          generatedBy: safeTrim(currentUserId) || null,
        },
      } as any);
      setStatus('Generated preview PDF.');
      if (opts?.open) setPreviewOpen(true);
    } catch (e: any) {
      setStatus(String(e?.message || 'Failed to generate preview.'));
    } finally {
      setBusy('idle');
    }
  };

  const sendPacket = async () => {
    if (!squadId) return;
    setBusy('sending');
    setStatus(null);
    try {
      if (!selectedTeacher) {
        setStatus('Please select a teacher.');
        setBusy('idle');
        return;
      }

      const toEmail = safeTrim(selectedTeacher.email);
      const toSlackEmail = safeTrim(selectedTeacher.slackEmail);
      const storagePath = safeTrim(previewStoragePath || parsed?.latestPdf?.storagePath);
      
      if (!storagePath) {
        setStatus('Generate a PDF first.');
        setBusy('idle');
        return;
      }
      if (!toEmail) {
        setStatus('Selected teacher has no email address.');
        setBusy('idle');
        return;
      }
      if (!toSlackEmail) {
        setStatus('Selected teacher has no Slack email address.');
        setBusy('idle');
        return;
      }

      const fn = httpsCallable(getFunctions(), 'sendFirstDayTeacherPacket');
      const result: any = await fn({
        squadId,
        storagePath,
        toEmail,
        toSlackEmail,
        sendVia: 'both',
        // Keep the message extremely short; the detailed checklist is in the PDF.
        intro: safeTrim(draft.introNote),
      });

      const now = Date.now();
      await updateDoc(doc(db, 'squads', squadId), {
        'firstDayTeacherPacket.sentAt': now,
        'firstDayTeacherPacket.sentBy': safeTrim(currentUserId) || null,
        'firstDayTeacherPacket.sentTo': { 
          teacherId: selectedTeacher.id,
          teacherName: selectedTeacher.displayName || null,
          email: toEmail || null, 
          slackEmail: toSlackEmail || null 
        },
      } as any);

      const slackSent = result?.data?.slackSent;
      const slackError = result?.data?.slackError;
      
      if (slackSent) {
        setStatus('Sent to both email and Slack.');
      } else if (slackError) {
        setStatus(`Sent to email. Slack failed: ${slackError}`);
      } else {
        setStatus('Sent to email.');
      }
    } catch (e: any) {
      console.error('Send failed:', e);
      const errorMsg = e?.message || e?.details?.message || String(e);
      setStatus(`Failed to send: ${errorMsg}`);
    } finally {
      setBusy('idle');
    }
  };

  const title = safeTrim(squad?.title) || 'Squad';

  const canonicalSquad = useMemo(() => ({
    classroomName: (squad as any)?.classroomName || '',
    hasProjectorOrTv: (squad as any)?.hasProjectorOrTv,
    projectorConnectionInstructions: (squad as any)?.projectorConnectionInstructions || '',
    additionalNotes: (squad as any)?.additionalNotes || '',
  }), [squad]);

  const normalizedSquadDraft = useMemo(() => ({
    classroomName: String((squadDraft as any).classroomName || ''),
    hasProjectorOrTv: (squadDraft as any).hasProjectorOrTv,
    projectorConnectionInstructions: String((squadDraft as any).projectorConnectionInstructions || ''),
    additionalNotes: String((squadDraft as any).additionalNotes || ''),
  }), [squadDraft]);

  const squadDirty = useMemo(() => {
    return JSON.stringify(normalizedSquadDraft) !== JSON.stringify(canonicalSquad);
  }, [normalizedSquadDraft, canonicalSquad]);

  const canonicalSchool = useMemo(() => ({
    wifiUsername: (site as any)?.wifiUsername || '',
    wifiPassword: (site as any)?.wifiPassword || '',
    arrivalProcedure: (site as any)?.arrivalProcedure || '',
    dismissalProcedure: (site as any)?.dismissalProcedure || '',
    lateArrivalProcedure: (site as any)?.lateArrivalProcedure || '',
    bathroomRules: (site as any)?.bathroomRules || '',
    hallwayRules: (site as any)?.hallwayRules || '',
    parkingInstructions: (site as any)?.parkingInstructions || '',
  }), [site]);

  const normalizedSchoolDraft = useMemo(() => ({
    wifiUsername: safeTrim((schoolDraft as any).wifiUsername),
    // Passwords can contain spaces; do not trim.
    wifiPassword: String((schoolDraft as any).wifiPassword || ''),
    arrivalProcedure: String((schoolDraft as any).arrivalProcedure || ''),
    dismissalProcedure: String((schoolDraft as any).dismissalProcedure || ''),
    lateArrivalProcedure: String((schoolDraft as any).lateArrivalProcedure || ''),
    bathroomRules: String((schoolDraft as any).bathroomRules || ''),
    hallwayRules: String((schoolDraft as any).hallwayRules || ''),
    parkingInstructions: String((schoolDraft as any).parkingInstructions || ''),
  }), [schoolDraft]);

  const schoolDirty = useMemo(() => {
    return JSON.stringify(normalizedSchoolDraft) !== JSON.stringify(canonicalSchool);
  }, [normalizedSchoolDraft, canonicalSchool]);

  const canonicalOps = useMemo(() => ({
    selectedTeacherId: safeTrim((initialDraft as any).selectedTeacherId),
    introNote: safeTrim((initialDraft as any).introNote),
    schoolSpecificNotes: safeTrim((initialDraft as any).schoolSpecificNotes),
    otherNotes: safeTrim((initialDraft as any).otherNotes),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [safeTrim((parsed as any)?.draft?.updatedAt)]);

  const opsDirty = useMemo(() => {
    const cur = {
      selectedTeacherId: safeTrim(draft.selectedTeacherId),
      introNote: safeTrim(draft.introNote),
      schoolSpecificNotes: safeTrim(draft.schoolSpecificNotes),
      otherNotes: safeTrim(draft.otherNotes),
    };
    return JSON.stringify(cur) !== JSON.stringify(canonicalOps);
  }, [draft, canonicalOps]);

  const canSave = schoolDirty || squadDirty || opsDirty;
  const saveHoverText = isBusy
    ? (busy === 'saving' ? 'Saving…' : 'Busy…')
    : canSave
      ? 'Unsaved changes — click to save'
      : 'Up to date — nothing to save';

  const cardStyle: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 12,
  };
  const sectionTitleStyle: React.CSSProperties = { fontWeight: 900, fontSize: 12 };
  const detailsStyle: React.CSSProperties = { ...cardStyle, padding: 0, overflow: 'hidden' };
  const detailsSummaryStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: '10px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    userSelect: 'none',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  };

  const shouldOpenOpsNotesByDefault =
    Boolean(safeTrim(draft.introNote) || safeTrim(draft.schoolSpecificNotes) || safeTrim(draft.otherNotes));

  return (
    <div style={{ width: '100%', display: 'grid', gap: 10, maxWidth: 980, margin: '0 auto', padding: '0 10px' }}>
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'grid', gap: 2 }}>
          <div style={{ fontWeight: 900, fontSize: 13 }}>{SOP_TITLE}</div>
          <div
            className="muted"
            style={{
              fontSize: 12,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '2px 8px',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                background: isBusy ? '#fff7ed' : (squadDirty || opsDirty) ? '#fff' : '#ecfdf5',
                color: isBusy ? '#9a3412' : (squadDirty || opsDirty) ? '#6b7280' : '#065f46',
                fontWeight: 700,
              }}
            >
              {isBusy ? 'Saving…' : (squadDirty || opsDirty) ? 'Unsaved changes' : 'Up to date'}
            </span>
            {lastSavedAt ? (
              <span>Last saved {new Date(lastSavedAt).toLocaleTimeString()}</span>
            ) : null}
            <span>
              {safeTrim(parsed?.sentAt) ? `Sent ${new Date(Number(parsed.sentAt)).toLocaleString()}` : 'Not sent yet'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            title={saveHoverText}
            style={{
              display: 'inline-flex',
              cursor: isBusy ? 'progress' : canSave ? 'pointer' : 'not-allowed',
            }}
          >
            <button
              type="button"
              className="mini-btn"
              onClick={() => void saveAll()}
              disabled={isBusy || !canSave}
              style={{
                background: canSave ? '#2563eb' : '#f3f4f6',
                color: canSave ? '#ffffff' : '#6b7280',
                borderColor: justSaved ? '#22c55e' : canSave ? '#1d4ed8' : '#e5e7eb',
                boxShadow: justSaved ? '0 0 0 3px rgba(34,197,94,0.15)' : undefined,
                opacity: isBusy ? 0.75 : 1,
                transition: 'box-shadow 200ms ease, border-color 200ms ease, background 120ms ease, color 120ms ease, opacity 120ms ease',
              }}
            >
              {busy === 'saving' ? 'Saving…' : justSaved ? 'Saved' : canSave ? 'Save' : 'Up to date'}
            </button>
          </span>
          <button
            type="button"
            className="mini-btn"
            onClick={() => void generatePreview({ open: true })}
            disabled={isBusy}
            title="Generates the PDF, then opens review/send"
          >
            {busy === 'generating' ? 'Generating…' : 'Send'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
        <div style={cardStyle}>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={sectionTitleStyle}>Recipient</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="muted" style={{ fontSize: 12, minWidth: 62 }}>Teacher</div>
              <div style={{ flex: '1 1 320px', maxWidth: 520 }}>
                <SearchableSelect
                  valueId={draft.selectedTeacherId || ''}
                  placeholder="Search teachers..."
                  options={teacherOptions}
                  disabled={loadingTeachers}
                  onSelectId={(id) => setDraft((d) => ({ ...d, selectedTeacherId: id }))}
                  showIdInMenu={false}
                />
              </div>
              {selectedTeacher ? (
                <div className="muted" style={{ fontSize: 12, flex: '2 1 360px', minWidth: 260 }}>
                  <strong style={{ color: '#111827' }}>{selectedTeacher.displayName || 'Unnamed'}</strong>
                  {safeTrim(selectedTeacher.email) ? ` • ${safeTrim(selectedTeacher.email)}` : ' • (no email)'}
                  {safeTrim((selectedTeacher as any).slackEmail) ? ` • Slack: ${safeTrim((selectedTeacher as any).slackEmail)}` : ' • (no Slack email)'}
                </div>
              ) : (
                <div className="muted" style={{ fontSize: 12 }}>Will send via email + Slack</div>
              )}
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div style={sectionTitleStyle}>First-day essentials (SSOT)</div>
              <div className="muted" style={{ fontSize: 12 }}>
                School-wide logistics (SSOT on School).
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                WiFi username
                <input
                  value={String((schoolDraft as any).wifiUsername || '')}
                  onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), wifiUsername: e.target.value } as any))}
                />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                WiFi password
                <input
                  value={String((schoolDraft as any).wifiPassword || '')}
                  onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), wifiPassword: e.target.value } as any))}
                />
              </label>
            </div>

            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Arrival procedure
              <AutoGrowTextarea
                value={String((schoolDraft as any).arrivalProcedure || '')}
                onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), arrivalProcedure: (e.target as any).value } as any))}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Dismissal procedure
              <AutoGrowTextarea
                value={String((schoolDraft as any).dismissalProcedure || '')}
                onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), dismissalProcedure: (e.target as any).value } as any))}
              />
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                Bathroom rules
                <AutoGrowTextarea
                  value={String((schoolDraft as any).bathroomRules || '')}
                  onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), bathroomRules: (e.target as any).value } as any))}
                />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                Hallway rules
                <AutoGrowTextarea
                  value={String((schoolDraft as any).hallwayRules || '')}
                  onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), hallwayRules: (e.target as any).value } as any))}
                />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                Classroom name
                <input
                  value={String((squadDraft as any).classroomName || '')}
                  onChange={(e) => setSquadDraft(d => ({ ...(d || {}), classroomName: e.target.value } as any))}
                />
              </label>
              <div />
            </div>

            <div className="muted" style={{ fontSize: 12 }}>
              Tip: Fill essentials first, then use “Everything else” only if needed.
            </div>
          </div>
        </div>

        <details style={detailsStyle}>
          <summary style={detailsSummaryStyle}>
            <div style={{ fontWeight: 900, fontSize: 12 }}>Everything else (optional)</div>
            <div className="muted" style={{ fontSize: 12 }}>Late arrival, parking, projector, teaching notes</div>
          </summary>
          <div style={{ padding: 12, display: 'grid', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Late arrival rules
              <AutoGrowTextarea
                value={String((schoolDraft as any).lateArrivalProcedure || '')}
                onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), lateArrivalProcedure: (e.target as any).value } as any))}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Parking instructions
              <AutoGrowTextarea
                value={String((schoolDraft as any).parkingInstructions || '')}
                onChange={(e) => setSchoolDraft(d => ({ ...(d || {}), parkingInstructions: (e.target as any).value } as any))}
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                Projector / TV available
                <select
                  value={String((squadDraft as any).hasProjectorOrTv ?? '')}
                  onChange={(e) => {
                    const v = (e.target as any).value;
                    setSquadDraft(d => ({ ...(d || {}), hasProjectorOrTv: v === '' ? undefined : v === 'true' } as any));
                  }}
                >
                  <option value="">Unknown</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              <div />
            </div>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              How to connect to projector
              <AutoGrowTextarea
                value={String((squadDraft as any).projectorConnectionInstructions || '')}
                onChange={(e) => setSquadDraft(d => ({ ...(d || {}), projectorConnectionInstructions: (e.target as any).value } as any))}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Teaching notes
              <AutoGrowTextarea
                value={String((squadDraft as any).additionalNotes || '')}
                onChange={(e) => setSquadDraft(d => ({ ...(d || {}), additionalNotes: (e.target as any).value } as any))}
              />
            </label>
            <div className="muted" style={{ fontSize: 12 }}>
              Use the Save button above to commit changes.
            </div>
          </div>
        </details>

        <details style={detailsStyle} open={shouldOpenOpsNotesByDefault}>
          <summary style={detailsSummaryStyle}>
            <div style={{ fontWeight: 900, fontSize: 12 }}>Ops notes (PDF-only)</div>
            <div className="muted" style={{ fontSize: 12 }}>Optional context for this send</div>
          </summary>
          <div style={{ padding: 12, display: 'grid', gap: 10 }}>
            {([
              ['Intro note (optional)', 'introNote'],
              ['School-specific note', 'schoolSpecificNotes'],
              ['Other notes', 'otherNotes'],
            ] as Array<[string, keyof PacketDraft]>).map(([label, key]) => (
              <label key={String(key)} style={{ display: 'grid', gap: 4, fontSize: 12 }}>
                {label}
                <AutoGrowTextarea
                  value={String((draft as any)[key] || '')}
                  onChange={(e) => setDraft((d) => ({ ...(d || {}), [key]: (e.target as any).value }))}
                  placeholder="Optional…"
                />
              </label>
            ))}
          </div>
        </details>
      </div>

      {status ? (
        <div className="muted" style={{ fontSize: 12 }}>
          {status}
        </div>
      ) : null}

      {previewOpen ? (
        <div className="pdf-modal-overlay" onClick={() => setPreviewOpen(false)} role="presentation">
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <div className="pdf-modal-title">Preview first-day packet</div>
              <button className="pdf-modal-close" onClick={() => setPreviewOpen(false)} aria-label="Close preview">✕</button>
            </div>
            {previewUrl ? (
              <iframe className="pdf-frame" title="First day packet PDF" src={previewUrl} />
            ) : (
              <div className="pdf-generating-screen">
                <span className="spinner spinner-lg" aria-hidden="true" />
                <div className="pdf-generating-text">Generating…</div>
              </div>
            )}
            <div className="send-preview-footer">
              <button className="btn btn-secondary" onClick={() => setPreviewOpen(false)} disabled={isBusy}>
                Cancel
              </button>
              <button className="btn btn-confirm" onClick={() => void sendPacket()} disabled={isBusy || !previewUrl}>
                Send
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
